// Paint project math + quantified accessory rules.
// Supports multiple rooms and a gallons/litres output toggle.
// Consumables get real quantities; tools are recommended at qty 1.
// Every assumption is surfaced to the user and adjustable.
import { toFeet, rectArea, applyWaste } from '../lib/calc'
import type { UnitSystem } from '../lib/calc'
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export interface Room {
  name: string
  length: number
  width: number
  height: number
}

export type PaintVolumeUnit = 'gallon' | 'litre'

// 'dimensions' = user enters rooms with L × W × H.
// 'area' = user already knows the total paintable area (walls + optional
// ceiling, minus openings) and types it directly.
export type PaintInputMode = 'dimensions' | 'area'

export interface PaintInputs {
  unit: UnitSystem // room measurements
  outputUnit: PaintVolumeUnit // show paint in gallons or litres
  inputMode: PaintInputMode
  rooms: Room[]
  totalArea: number  // sq ft (imperial) or m² (metric) — used in 'area' mode
  includeCeiling: boolean
  doors: number
  windows: number
  coats: number
  coveragePerGallon: number // sq ft per gallon per coat
  wastePct: number
  needPrimer: boolean
  gallonsHave: number // reverse path: "I have N gallons — what area can I paint?"
}

export interface Volume {
  qty: number
  unit: PaintVolumeUnit
}

export interface PaintResult {
  paintableArea: number // sq ft, after openings
  paint: Volume
  primer: Volume
  lines: AccessoryLine[]
  perimeterFt: number
}

export const DEFAULT_PAINT: PaintInputs = {
  unit: 'imperial',
  outputUnit: 'gallon',
  inputMode: 'dimensions',
  // Name starts empty so the "Select a room" placeholder is the dropdown's
  // default. Dimensions are neutral 12×12×8 ft — gives a reasonable initial
  // result until the user picks a room type.
  rooms: [{ name: '', length: 12, width: 12, height: 8 }],
  totalArea: 348,  // 2*(12+12)*8 - 21 - 15 = 348 sq ft (matches default room with 1 door + 1 window)
  includeCeiling: false,
  doors: 1,
  windows: 1,
  coats: 2,
  coveragePerGallon: 350,
  wastePct: 10,
  needPrimer: true,
  gallonsHave: 2,
}

/**
 * Reverse calculation: how much area can you paint with the gallons (or litres)
 * you already have? Useful for leftover-paint projects.
 *
 *   coverage (sq ft) = gallons × coveragePerGallon ÷ coats
 *
 * Example: 2 gallons × 350 sq ft/gal ÷ 2 coats = 350 sq ft of wall.
 */
export interface PaintCoverageResult {
  totalGallons: number    // normalized to gallons regardless of input unit
  totalLitres: number     // mirror in litres
  coverageSqft: number    // imperial area
  coverageM2: number      // metric area
  equivSquareFt: number   // side of equivalent square wall, ft
  equivSquareM: number    // side of equivalent square wall, m
}

export function computePaintCoverage(
  amount: number,
  amountUnit: PaintVolumeUnit,
  coveragePerGallon: number,
  coats: number,
): PaintCoverageResult {
  const safeAmount = Math.max(0, amount)
  const safeCov = Math.max(50, coveragePerGallon)
  const safeCoats = Math.max(1, coats)
  // Normalize input to gallons for the math.
  const gallons = amountUnit === 'litre' ? safeAmount / L_PER_GAL : safeAmount
  const coverageSqft = (gallons * safeCov) / safeCoats
  return {
    totalGallons: round(gallons, 2),
    totalLitres: round(gallons * L_PER_GAL, 2),
    coverageSqft: round(coverageSqft, 0),
    coverageM2: round(coverageSqft * 0.09290304, 1),
    equivSquareFt: round(Math.sqrt(coverageSqft), 1),
    equivSquareM: round(Math.sqrt(coverageSqft * 0.09290304), 2),
  }
}

const DOOR_SQFT = 21 // 3 x 7 ft
const WINDOW_SQFT = 15 // 3 x 5 ft
const TAPE_FT_PER_ROLL = 180 // 60 yd roll
const CLOTH_SQFT = 108 // 9 x 12 ft drop cloth
const CAULK_FT_PER_TUBE = 56
const L_PER_GAL = 3.785411784

function toVolume(gallons: number, out: PaintVolumeUnit): Volume {
  if (gallons <= 0) return { qty: 0, unit: out }
  if (out === 'litre') return { qty: Math.max(0.5, ceilTo(gallons * L_PER_GAL, 0.5)), unit: 'litre' }
  // Round up to the nearest quart (a real size) — never pad to a whole gallon.
  return { qty: Math.max(0.25, ceilTo(gallons, 0.25)), unit: 'gallon' }
}

// Smart can-size planner — translates the math volume into "what to actually
// pick up at the store" buy list. Uses the active output unit to decide which
// real-world can sizes to recommend:
//   Gallons mode (US)  → 1 qt for tiny, 1-gal cans for the bulk + quarts to top
//                        up the remainder, 5-gal pail for big jobs (≥5 gal)
//   Litres mode (CA)   → 1 L for tiny, 4-L cans for the bulk + 1-L cans to top
//                        up, 18.9 L pail when it gets large (≥20 L)
// Returns one or more sizes whose total just covers `qty`, adding a SMALLER
// top-up can rather than a wasteful extra full one (since 1 gal = 4 qt, 2.25 gal
// → 2 gal + 1 qt, not 3 gal; 4.5 L → 4 L + 1 L, not 2 × 4 L). Empty when none.
export interface PaintCanPart {
  count: number
  sizeLabel: string   // '1 qt', '1 gal', '5-gal pail', '1 L', '4 L', '18.9 L pail'
}

export function planPaintCans(qty: number, unit: PaintVolumeUnit): PaintCanPart[] {
  if (qty <= 0) return []
  const parts: PaintCanPart[] = []
  if (unit === 'gallon') {
    if (qty <= 0.25) return [{ count: 1, sizeLabel: '1 qt' }]
    // Anything ≥ 5 gal → 5-gal pails (cheaper per gallon than 5 × 1-gal cans)
    if (qty >= 5) return [{ count: Math.ceil(qty / 5), sizeLabel: '5-gal pail' }]
    let fullGal = Math.floor(qty)
    const quarts = Math.ceil((qty - fullGal) / 0.25) // 0..4 quarts for the remainder
    // 4 quarts === a whole gallon: fold it into the gallon count instead.
    if (quarts >= 4) fullGal += 1
    if (fullGal > 0) parts.push({ count: fullGal, sizeLabel: '1 gal' })
    if (quarts > 0 && quarts < 4) parts.push({ count: quarts, sizeLabel: '1 qt' })
    return parts
  }
  // Litres mode
  if (qty <= 1) return [{ count: 1, sizeLabel: '1 L' }]
  // ≥ 20 L (would be 5+ × 4-L cans) → 18.9 L pail
  if (qty >= 20) return [{ count: Math.ceil(qty / 18.9), sizeLabel: '18.9 L pail' }]
  let full4 = Math.floor(qty / 4)
  const oneL = Math.ceil(qty - full4 * 4) // 0..4 litres for the remainder
  // A 3–4 L remainder is fewer cans bought as one more 4-L than as 3–4 × 1-L.
  if (oneL >= 3) full4 += 1
  if (full4 > 0) parts.push({ count: full4, sizeLabel: '4 L' })
  if (oneL > 0 && oneL < 3) parts.push({ count: oneL, sizeLabel: '1 L' })
  return parts
}

export function computePaint(i: PaintInputs, locale: Locale = 'en'): PaintResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  let perimeterFt = 0
  let wallArea = 0
  let ceilingArea = 0
  let floorArea = 0
  let paintableArea = 0

  if (i.inputMode === 'area') {
    // User-entered total paintable area (already net of openings, ceiling
    // included only if they chose to). perimeter/floor are unknown, so the
    // perimeter-based accessories (tape, caulk, cloths) fall back to area-
    // based heuristics in the lines section below.
    paintableArea = i.unit === 'metric' ? Math.max(0, i.totalArea) * 10.7639104 : Math.max(0, i.totalArea)
  } else {
    for (const room of i.rooms ?? []) {
      const wF = toFeet(room.width, i.unit)
      const lF = toFeet(room.length, i.unit)
      const hF = toFeet(room.height, i.unit)
      perimeterFt += 2 * (Math.max(0, wF) + Math.max(0, lF))
      wallArea += 2 * (Math.max(0, wF) + Math.max(0, lF)) * Math.max(0, hF)
      floorArea += rectArea(wF, lF)
      if (i.includeCeiling) ceilingArea += rectArea(wF, lF)
    }
    const openings = i.doors * DOOR_SQFT + i.windows * WINDOW_SQFT
    paintableArea = Math.max(0, wallArea + ceilingArea - openings)
  }
  const areaWithWaste = applyWaste(paintableArea, i.wastePct)
  const cov = Math.max(1, i.coveragePerGallon)

  const rawPaintGal = paintableArea > 0 ? (areaWithWaste * i.coats) / cov : 0
  const rawPrimerGal = i.needPrimer && paintableArea > 0 ? areaWithWaste / cov : 0
  const paint = toVolume(rawPaintGal, i.outputUnit)
  const primer = toVolume(rawPrimerGal, i.outputUnit)

  const lines: AccessoryLine[] = []

  if (rawPrimerGal > 0) {
    lines.push({
      item: 'paint primer', label: tr('Primer', 'Apprêt'), kind: 'consumable',
      qty: primer.qty, unit: primer.unit,
      note: tr('One coat over bare, patched, or colour-changing surfaces.', 'Une couche sur les surfaces nues, réparées ou changeant de couleur.'),
    })
  }

  // In dimensions mode we know exact edges. In area mode we approximate at
  // ~0.5 ft of tape per sq ft of paintable area — calibrated to typical rooms.
  const isAreaMode = i.inputMode === 'area'
  const tapeFt = isAreaMode ? paintableArea * 0.5 : perimeterFt * 2 + i.doors * 17 + i.windows * 13
  lines.push({
    item: 'painters tape', label: tr("Painter's tape", 'Ruban à peinture'), kind: 'consumable',
    qty: Math.max(1, ceilTo(tapeFt / TAPE_FT_PER_ROLL, 1)), unit: 'roll',
    note: isAreaMode
      ? tr(`${TAPE_FT_PER_ROLL} ft per roll · area-based estimate · switch to Dimensions for exact edges`, `${TAPE_FT_PER_ROLL} pi par rouleau · estimation à partir de la surface · basculez à Dimensions pour le décompte exact`)
      : tr(`≈ ${Math.round(tapeFt)} ft of edges · ${TAPE_FT_PER_ROLL} ft per roll`, `≈ ${Math.round(tapeFt)} pi de bordures · ${TAPE_FT_PER_ROLL} pi par rouleau`),
  })

  lines.push({
    item: 'paint roller covers', label: tr('Roller covers', 'Manchons de rouleau'), kind: 'consumable',
    qty: Math.max(1, i.coats), unit: 'cover', note: tr('About one cover per coat.', 'Environ un manchon par couche.'),
  })

  lines.push({
    item: 'paint tray liners', label: tr('Tray liners', 'Doublures de bac'), kind: 'consumable',
    qty: Math.max(1, i.coats), unit: 'liner', note: tr('One per coat keeps cleanup quick.', 'Une par couche pour un nettoyage rapide.'),
  })

  // Cloths: in area mode we approximate floor area as ~1/3 of paintable area
  // (typical 8-ft ceiling, 4 walls + ceiling). Caulk: ~0.25 ft per sq ft.
  const clothFloorEst = isAreaMode ? paintableArea / 3 : floorArea
  lines.push({
    item: 'canvas drop cloth', label: tr('Drop cloths', 'Toiles de protection'), kind: 'consumable',
    qty: Math.max(1, ceilTo(clothFloorEst / CLOTH_SQFT, 1)), unit: 'cloth',
    note: isAreaMode
      ? tr(`Area-based estimate · switch to Dimensions for an exact floor count`, `Estimation à partir de la surface · basculez à Dimensions pour le décompte exact du plancher`)
      : tr(`Covers ≈ ${Math.round(floorArea)} sq ft of floor`, `Couvre ≈ ${Math.round(floorArea)} pi² de plancher`),
  })

  const caulkFt = isAreaMode ? paintableArea * 0.25 : perimeterFt + i.doors * 17 + i.windows * 13
  lines.push({
    item: 'paintable caulk', label: tr('Paintable caulk', 'Scellant peinturable'), kind: 'consumable',
    qty: Math.max(1, ceilTo(caulkFt / CAULK_FT_PER_TUBE, 1)), unit: 'tube',
    note: tr('For sealing trim gaps before painting.', 'Pour sceller les jeux des moulures avant de peindre.'), optional: true,
  })

  // Tools — recommended once, not "calculated".
  lines.push({ item: 'angled paint brush', label: tr('Angled sash brush', 'Pinceau à rechampir biseauté'), kind: 'tool', note: tr('For cutting in edges & trim.', 'Pour les coupes et les moulures.') })
  lines.push({ item: 'paint roller frame', label: tr('Roller frame + tray', 'Monture de rouleau + bac'), kind: 'tool' })
  lines.push({ item: 'paint extension pole', label: tr('Extension pole', 'Manche télescopique'), kind: 'tool', optional: true })
  if (rawPaintGal > 3) {
    lines.push({ item: '5 gallon bucket paint grid', label: tr('5-gal bucket + grid', 'Seau de 5 gal + grille'), kind: 'tool', note: tr('Faster than a tray on big jobs.', 'Plus rapide qu’un bac pour les gros travaux.'), optional: true })
  }

  return {
    paintableArea: round(paintableArea, 0),
    paint,
    primer,
    lines,
    perimeterFt: round(perimeterFt, 0),
  }
}
